import {
  useRoute,
  useNavigation,
  useIsFocused,
} from '@react-navigation/native';
import { FlashList } from '@shopify/flash-list';
import React, { useEffect, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
  DeviceEventEmitter,
  Image,
  Pressable,
  TouchableOpacity,
  ScrollView,
  ActivityIndicator,
} from 'react-native';
import { useToast } from 'react-native-toast-notifications';

import { WebHoverBox } from '@components/Modals/WebHoverBox';
import { ConvertedToTaskToast } from '@components/ProjectDetail/ProjectChecklist/ConvertedToTask';
import { ProjectChecklistCenterModal } from '@components/ProjectDetail/ProjectChecklist/ProjectChecklistCenterModal';
import {
  applySelectAll,
  getSelectAllState,
} from '@components/ProjectDetail/ProjectChecklist/selectAllState';
import { useProjectChecklistLogic } from '@components/ProjectDetail/ProjectChecklist/useProjectChecklistLogic';
import { Box, Text } from '@components/Restyle/index';
import Button from '@components/shared/Button/Button';
import Icon from '@components/shared/Icon/Icon';
import {
  ChecklistRowSkeleton,
  SkeletonList,
} from '@components/shared/skeletons';
import { UpgradePaywallTooltip } from '@components/shared/UpgradePaywallTooltip';
import { TaskCreatedToast } from '@components/TaskList/TaskCreatedToast.web';
import ToggleSwitch from '@components/Tasks/ToggleSwitch.web';
import { CheckBoxButton } from '@components/Web/CheckBoxButton';
import { useWebDrawer } from '@components/Web/Drawer/WebDrawerContext';
import { TooltipOnboardingWeb } from '@components/Web/TooltipOnboarding.web';
import {
  ProjectChecklist,
  ProjectMemberRole,
  useConvertProjectChecklistToTaskMutation,
  TeamMemberRole,
  useUpdateProjectChecklistSortMutation,
  useUpdateOnboardingStateMutation,
} from '@graphql/generated';
import { useChecklistTemplate } from '@hooks/useChecklistTemplate';
import { useHover } from '@hooks/useHover';
import useMe from '@hooks/useMe';
import { useOnboardingActive } from '@hooks/useOnboardingActive';
import { useProjectFromQuery } from '@hooks/useProjectFromQuery';
import { DndKitSortableProjectChecklistListWeb } from '@navigation/projects/DndKitSortableProjectChecklistList.web';
import { ProjectAddChecklistItem } from '@navigation/projects/ProjectAddChecklistItem';
import { ProjectsStackScreenProps } from '@navigation/projects/projects-stack';
import Images from '@themes/images';
import theme, { getScaledSize, SCALED_DIMENSIONS } from '@themes/theme';
import { shouldShowPaywallForProject } from '@utils/teams/paywall';

// TA-10891 #16: one Select Checklist row — the whole row toggles, and it
// takes the grey01 hover tint edge to edge. The row carries the modal's
// horizontal padding itself (the modal body has none) because FlashList clips
// a negative-margin bleed.
const SelectChecklistRow: React.FC<{
  checked: boolean;
  indeterminate?: boolean;
  onPress: () => void;
  children: React.ReactNode;
}> = ({ checked, indeterminate = false, onPress, children }) => {
  const { isHovered, hoverProps } = useHover();
  return (
    <Pressable
      {...hoverProps}
      accessibilityRole='checkbox'
      accessibilityState={{ checked: indeterminate ? 'mixed' : checked }}
      onPress={onPress}
      style={{
        paddingHorizontal: theme.spacing.l,
        backgroundColor: isHovered ? theme.colors.grey01 : 'transparent',
      }}>
      <Box
        height={52}
        flexDirection='row'
        alignItems='center'
        borderBottomColor='grey02'
        borderBottomWidth={1}>
        <CheckBoxButton checked={checked} indeterminate={indeterminate} />
        <Box flex={1} minWidth={0}>
          {children}
        </Box>
      </Box>
    </Pressable>
  );
};

const SelectChecklistEmptyFooter: React.FC<{ isEmpty: boolean }> = ({
  isEmpty,
}) => {
  if (!isEmpty) return null;
  return (
    <Box height={52} justifyContent='center' px='l'>
      <Text variant='bodySecondaryNew' ellipsizeMode='tail' textAlign='left'>
        No items to convert.
      </Text>
    </Box>
  );
};

export const ProjectChecklistPage = () => {
  const { t } = useTranslation('models');
  const toast = useToast();
  const toastIdRef = useRef<string | null>(null);
  const { me } = useMe();
  const onboardingActive = useOnboardingActive();

  const [updateOnboardingState] = useUpdateOnboardingStateMutation();
  const skipChecklistCompleteOnNextBlurRef = useRef(false);

  const route =
    useRoute<ProjectsStackScreenProps<'project-checklist'>['route']>();
  const {
    params: { projectUUId },
  } = route;
  const [showAddChecklistScreen, setShowAddChecklistScreen] = useState(false);

  const [errorTitle, setErrorTitle] = useState('');
  const [errorMsg, setErrorMsg] = useState('');
  const [itemWidth, setItemWidth] = useState(500);

  const [modalVisible, setModalVisible] = useState(false);
  const [showAddCheckListItem, setShowAddCheckListItem] = useState(false);
  const [showChecklistActionTooltip, setShowChecklistActionTooltip] =
    useState(false);
  const [showConvertToTasksTooltip, setShowConvertToTasksTooltip] =
    useState(false);
  const cameFromTooltipRef = useRef(false);
  const convertFromTooltipRef = useRef(false);
  const navigation = useNavigation();

  const [selectChecklistModalVisible, setSelectChecklistModalVisible] =
    useState(false);

  const [showChecklistCompletedItems, setShowChecklistCompletedItems] =
    useState(false);
  const scrollViewRef = React.useRef<ScrollView>(null);
  const { project } = useProjectFromQuery({
    projectUUId,
  });

  const { creatingChecklist } = useChecklistTemplate(projectUUId);

  const {
    idProjectChecklistTemplateOpen,
    setIdProjectChecklistTemplateOpen,
    idProjectSaveChecklistAsTemplateOpen,
    setIdProjectSaveChecklistAsTemplateOpen,
    setPaywallModalOpen,
    setPaywallTeam,
  } = useWebDrawer();

  const {
    checklist,
    completedChecklist,
    processing,
    setProcessing,
    listProjectChecklist,
    loading,
    refetch,
    sortChecklist,
    doAddItem,
    onDelete,
    onSelect,
    updateProjectChecklist,
  } = useProjectChecklistLogic({
    projectUUId,
    beforeAddItem: () => {
      if (shouldShowPaywallForProject(project)) {
        setPaywallTeam(project?.team ?? undefined);
        setPaywallModalOpen(true);
        return false;
      }
      return true;
    },
  });

  const [refreshing, setRefreshing] = useState(false);

  const isFocused = useIsFocused();
  const showChecklistActionTooltipRef = useRef(showChecklistActionTooltip);
  useEffect(() => {
    showChecklistActionTooltipRef.current = showChecklistActionTooltip;
  }, [showChecklistActionTooltip]);

  useEffect(() => {
    if (!isFocused) {
      // nothing to clear now — TaskChecklistItem manages its own edit state
    }
  }, [isFocused]);

  useEffect(() => {
    if (idProjectChecklistTemplateOpen === '-1') {
      setRefreshing(true);
      refetch().then(() => {
        setRefreshing(false);
        setIdProjectChecklistTemplateOpen('');
        if (cameFromTooltipRef.current) {
          cameFromTooltipRef.current = false;
          setShowConvertToTasksTooltip(true);
        }
      });
    } else if (
      idProjectChecklistTemplateOpen === '' &&
      cameFromTooltipRef.current
    ) {
      // Template picker dismissed without applying — clear the onboarding intent
      cameFromTooltipRef.current = false;
    }
  }, [idProjectChecklistTemplateOpen]);

  useEffect(() => {
    if (
      idProjectSaveChecklistAsTemplateOpen === '-1' ||
      idProjectSaveChecklistAsTemplateOpen === '-2'
    ) {
      const toastId = showToast(
        idProjectSaveChecklistAsTemplateOpen === '-1'
          ? t('models:projects.checklist.templateCreated')
          : t('models:projects.checklist.templateUpdated'),
        () => {
          setIdProjectSaveChecklistAsTemplateOpen('');
          setIdProjectChecklistTemplateOpen(projectUUId);
          setTimeout(() => toast.hide(toastId), 0);
        },
        () => {
          setIdProjectSaveChecklistAsTemplateOpen('');
        }
      );
    }
  }, [idProjectSaveChecklistAsTemplateOpen]);

  const showActionTooltipParam = (route.params as any)?.showActionTooltip;

  useEffect(() => {
    if (!showActionTooltipParam) return;

    const timeoutId = setTimeout(() => {
      setShowChecklistActionTooltip(true);
      navigation.setParams({ showActionTooltip: undefined } as any);
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [navigation, showActionTooltipParam]);

  const updateChecklistCreationCompleteState = async () => {
    await updateOnboardingState({
      variables: { attributes: { checklistCreationComplete: true } },
      update: (cache) => {
        if (!me?.id) return;
        cache.modify({
          id: cache.identify({ __typename: 'User', id: me.id }),
          fields: {
            onboardingState: (existing) => ({
              ...existing,
              checklistCreationComplete: true,
            }),
          },
        });
      },
    });
  };

  useEffect(() => {
    if (
      !isFocused &&
      showChecklistActionTooltip &&
      me?.onboardingState?.checklistCreationComplete === false
    ) {
      if (skipChecklistCompleteOnNextBlurRef.current) {
        skipChecklistCompleteOnNextBlurRef.current = false; // reset
        return;
      }
      updateChecklistCreationCompleteState();
    }
  }, [
    isFocused,
    showChecklistActionTooltip,
    me?.onboardingState?.checklistCreationComplete,
  ]);

  useEffect(() => {
    if (checklist.length > 0 && showActionTooltipParam) {
      updateChecklistCreationCompleteState();
    }
  }, [checklist.length, showActionTooltipParam]);

  useEffect(() => {
    DeviceEventEmitter.emit(
      showChecklistActionTooltip
        ? 'CHECKLIST_ACTION_TOOLTIP_ACTIVE'
        : 'CHECKLIST_ACTION_TOOLTIP_INACTIVE'
    );
  }, [showChecklistActionTooltip]);

  const [convertProjectChecklistToTask] =
    useConvertProjectChecklistToTaskMutation();

  const disableMode =
    !(
      project?.currentUserRole === ProjectMemberRole.Owner ||
      project?.currentUserRole === ProjectMemberRole.Admin ||
      project?.currentUserRole === ProjectMemberRole.Editor ||
      project?.currentUserTeamRole === TeamMemberRole.Owner ||
      project?.currentUserTeamRole === TeamMemberRole.Admin
    ) || project?.archivedAt !== null;

  // TA-11121: one source of truth — archived projects are read-only for every
  // role, matching native.
  const canEdit = !disableMode;

  const [checklistItems, setChecklistItems] = useState([]);

  useEffect(() => {
    sortChecklist(listProjectChecklist as ProjectChecklist[]);
    setChecklistItems(
      listProjectChecklist
        .filter((t) => t?.completedAt === null)
        .map((item) => ({
          ...item,
          checked: false,
        })) ?? []
    );
  }, [listProjectChecklist]);

  const doRestChecklistItems = () => {
    checklistItems.length > 0 &&
      setChecklistItems(
        checklistItems.map((item) => ({
          ...item,
          checked: false,
        })) ?? []
      );
  };

  const gotoProjectTasks = () => {
    navigation.navigate('projects-stack', {
      screen: 'details',
      params: {
        projectUUId,
        screen: 'tasks',
      },
    });
  };

  const showToast = (msg: string, onPress: () => void, onClose: () => void) => {
    return toast.show(
      <ConvertedToTaskToast
        message={msg}
        duration={3000}
        onPress={() => {
          onPress();
        }}
      />,
      {
        type: 'custom',
        duration: 3000,
        placement: 'bottom',
        animationType: 'slide-in',
        style: {
          marginBottom: 39,
          backgroundColor: 'transparent',
          shadowColor: 'transparent',
        },
        onClose: () => {
          onClose();
        },
      }
    );
  };

  const [updateProjectChecklistSort] = useUpdateProjectChecklistSortMutation();

  const onDragEnd = (data: ProjectChecklist[]) => {
    if (!canEdit) return;
    setProcessing(true);
    const list = data.map((item: ProjectChecklist, index: number) => {
      return {
        projectChecklistId: item.id,
        sort: index + 1,
      };
    });
    updateProjectChecklistSort({
      variables: {
        projectUUId,
        attributes: list,
      },
      refetchQueries: ['listProjectChecklist'],
      onCompleted: () => {
        setProcessing(false);
      },
      onError: () => {
        setProcessing(false);
      },
    });
  };

  // Called by ProjectChecklistItem when saving an edited item name, or adding a new one
  const handleSaveChecklistItem = (name: string, item?: ProjectChecklist) => {
    if (!name.trim()) return;
    if (disableMode) return;

    if (item) {
      // Edit existing item
      if (name === item.name) return;
      if (checklist.some((c) => c.name === name && c.id !== item.id)) {
        setErrorTitle(t('models:projects.checklist.alreadyInChecklist'));
        setErrorMsg(
          t('models:projects.checklist.youAlreadyHaveAChecklistNamed') +
            " '" +
            name +
            "'."
        );
        setModalVisible(true);
        return;
      }
      setProcessing(true);
      updateProjectChecklist({
        variables: {
          projectChecklistId: item.id,
          attributes: {
            name,
            completed: item.completedAt !== null,
            sort: item.sort,
          },
        },
      });
    } else {
      // Add new item
      if (checklist.some((c) => c.name === name)) {
        setErrorTitle(t('models:projects.checklist.alreadyInChecklist'));
        setErrorMsg(
          t('models:projects.checklist.youAlreadyHaveAChecklistNamed') +
            " '" +
            name +
            "'."
        );
        setModalVisible(true);
        return;
      }
      doAddItem(name).then(() => {
        setShowAddCheckListItem(false);
      });
    }
  };

  // Called by TaskChecklistItem when deleting an item
  const handleDeleteChecklistItem = (itemId: string) => {
    if (!canEdit) return;
    const item = [...checklist, ...completedChecklist].find(
      (c) => c.id === itemId
    );
    if (item) onDelete(item as ProjectChecklist);
  };

  // Called by ProjectChecklistItem when toggling complete
  const handleCompletedChecklistItem = (
    item: ProjectChecklist,
    callback?: () => void
  ) => {
    if (!canEdit) return;
    onSelect(item);
    callback?.();
  };

  const doConvertToTasks = (selectedIds: ProjectChecklist['id'][]) => {
    if (shouldShowPaywallForProject(project)) {
      setPaywallTeam(project?.team ?? undefined);
      setPaywallModalOpen(true);
      return;
    }
    if (!convertFromTooltipRef.current) {
      toastIdRef.current = showToast(
        t('models:projects.checklist.convertedToTask'),
        () => {
          toastIdRef.current &&
            setTimeout(() => toast.hide(toastIdRef.current), 0);
          gotoProjectTasks();
        },
        () => {}
      );
    }

    convertProjectChecklistToTask({
      variables: {
        projectChecklistIds: [...selectedIds],
      },
      onCompleted: () => {
        if (convertFromTooltipRef.current) {
          convertFromTooltipRef.current = false;
          let onboardingToastId: string;
          onboardingToastId = toast.show(
            <TaskCreatedToast
              minWidth={388}
              hasRightArrow={true}
              title={t('models:projects.checklist.convertedToTask')}
              message={t(
                'models:projects.checklist.convertedToTaskOnboardingMsg'
              )}
              duration={3000}
              onPress={() => {
                toast.hide(onboardingToastId);
                updateChecklistCreationCompleteState();
                gotoProjectTasks();
              }}
            />,
            {
              type: 'custom',
              duration: 3000,
              placement: 'bottom',
              animationType: 'slide-in',
              style: {
                marginBottom: 39,
                backgroundColor: 'transparent',
                shadowColor: 'transparent',
              },
              onClose: () => {
                updateChecklistCreationCompleteState();
                gotoProjectTasks();
              },
            }
          );
        } else {
          if (toastIdRef.current) {
            toast.update(
              toastIdRef.current,
              <ConvertedToTaskToast
                message={t('models:projects.checklist.convertedToTask')}
                duration={0}
                onPress={() => {
                  toastIdRef.current &&
                    setTimeout(() => toast.hide(toastIdRef.current), 0);
                  gotoProjectTasks();
                }}
              />,
              {
                duration: 0,
              }
            );
            setTimeout(() => {
              if (toastIdRef.current) {
                toast.hide(toastIdRef.current);
                toastIdRef.current = null;
              }
            }, 2000);
          }
        }
      },
      onError: () => {
        console.log(
          'Error happened when exec convertProjectChecklistToTask, ',
          selectedIds
        );
      },
      refetchQueries: ['listProjectChecklist', 'getProject', 'listTasks'],
    });
  };

  const [confirmModalVisible, setConfirmModalVisible] = useState(false);

  const toggleItemChecked = (id: string) => {
    setChecklistItems((prevItems) =>
      prevItems.map((item) =>
        item.id === id ? { ...item, checked: !item.checked } : item
      )
    );
  };

  const disabledSelectChecklistConfirmButton =
    checklistItems.filter((item) => item.checked).length === 0;

  // Shared, unit-tested Select All rules (same as the native convert modal):
  // someChecked drives the indeterminate (minus) box.
  const {
    allChecked: allChecklistItemsChecked,
    someChecked: someChecklistItemsChecked,
  } = getSelectAllState(checklistItems);

  const toggleAllItemsChecked = () => {
    setChecklistItems((prevItems) => applySelectAll(prevItems));
  };

  const titleBarButton = (
    title: string,
    iconName: keyof typeof Images,
    onPress: () => void,
    disabled = false
  ) => {
    const color = disabled ? 'grey03' : 'black';
    return (
      <TouchableOpacity
        onPress={onPress}
        accessibilityLabel={title}
        disabled={disabled}>
        <WebHoverBox
          borderRadius='xs'
          px='m'
          height={32}
          backgroundColor='white'
          hoverBackgroundColor={disabled ? 'white' : 'grey01'}
          alignItems='center'
          justifyContent='center'>
          <Box flexDirection='row'>
            <Icon color={color} variant='s' name={iconName}></Icon>
            <Text ml='xs' variant='labelSmall' color={color}>
              {title}
            </Text>
          </Box>
        </WebHoverBox>
      </TouchableOpacity>
    );
  };

  const renderEmptyActiveComponent = () => {
    return (
      <Box flex={1}>
        {checklist.length === 0 && !loading && !creatingChecklist && (
          <Box
            accessibilityLabel='Project Checklist Bar'
            ml='m'
            mr='l'
            mt='m'
            mb='xxs'
            flexDirection='row'
            justifyContent='flex-start'>
            <Box
              style={{
                flex: 1,
                flexDirection: 'row',
                flexWrap: 'wrap',
              }}></Box>
            <>
              <UpgradePaywallTooltip
                show={!!project && shouldShowPaywallForProject(project)}>
                {titleBarButton(
                  t('projects.checklist.startFromScratch'),
                  'Plus',
                  () => {
                    if (shouldShowPaywallForProject(project)) {
                      setPaywallTeam(project?.team ?? undefined);
                      setPaywallModalOpen(true);
                      return;
                    }
                    setShowAddChecklistScreen(true);
                  },
                  disableMode
                )}
              </UpgradePaywallTooltip>

              <TooltipOnboardingWeb
                variant='bottom-right'
                tooltipStyle='success'
                title={t('projects.checklist.checklistActionTooltip.title')}
                description={t(
                  'projects.checklist.checklistActionTooltip.description'
                )}
                step={t('projects.checklist.checklistActionTooltip.step')}
                open={onboardingActive && showChecklistActionTooltip}
                forceShow={onboardingActive && showChecklistActionTooltip}
                arrowAtTriggerCenter>
                <UpgradePaywallTooltip
                  show={!!project && shouldShowPaywallForProject(project)}>
                  {titleBarButton(
                    t('projects.checklist.useTemplate'),
                    'FileText',
                    () => {
                      if (shouldShowPaywallForProject(project)) {
                        setPaywallTeam(project?.team ?? undefined);
                        setPaywallModalOpen(true);
                        return;
                      }
                      skipChecklistCompleteOnNextBlurRef.current = true;
                      if (showChecklistActionTooltip) {
                        cameFromTooltipRef.current = true;
                        setShowChecklistActionTooltip(false);
                      }
                      setIdProjectChecklistTemplateOpen(projectUUId);
                    },
                    disableMode
                  )}
                </UpgradePaywallTooltip>
              </TooltipOnboardingWeb>
            </>
          </Box>
        )}
        {creatingChecklist && (
          <Box flex={1} justifyContent='center' alignItems='center'>
            <Image
              style={{ width: 100, height: 100 }}
              source={require('../../../../assets/images/loadinganimation.gif')}
            />
          </Box>
        )}
        {!creatingChecklist && (
          <Box
            m='m'
            style={{
              flex: 1,
              alignItems: 'center',
              justifyContent: 'center',
            }}>
            <Image
              style={{ width: 139, height: 196 }}
              source={require('../../../../assets/images/checklist.png')}
            />
            <Text mt='l' variant='heading2' color='black'>
              Create a Project Checklist
            </Text>
            <Text
              mt='xs'
              mb='l'
              variant='bodySecondary'
              textAlign='center'
              color='grey04'>
              Plan and track work across the whole project. Start with a
              template or build your own.
            </Text>
          </Box>
        )}
      </Box>
    );
  };

  const buttonDisableMode = disableMode;

  const disableButton = (width: number, isPLus: boolean) => {
    return (
      <Box
        marginRight='xxxs'
        marginLeft='xs'
        width={width}
        height={getScaledSize(SCALED_DIMENSIONS.BUTTON_HEIGHT)}
        borderRadius='xs'
        alignItems='center'
        justifyContent='center'
        borderWidth={2}
        borderColor='grey02'>
        <Box flexDirection='row' alignItems='center'>
          <Icon
            variant='s'
            name={isPLus ? 'Plus' : 'Save'}
            color='grey04'
            marginRight='none'
          />
          <Text ml='xs' variant='labelSmall' color='grey04'>
            {isPLus
              ? t('projects.checklist.addItemFromTemplate')
              : t('projects.checklist.saveAsTemplate')}
          </Text>
        </Box>
      </Box>
    );
  };

  if (loading || refreshing)
    return (
      <Box flex={1} m='m'>
        <SkeletonList row={(index) => <ChecklistRowSkeleton key={index} />} />
      </Box>
    );
  if (!loading && listProjectChecklist.length === 0 && !showAddChecklistScreen)
    return renderEmptyActiveComponent();

  const hasCompletedItems = completedChecklist.length > 0;

  const addButton = (
    onPress: () => void,
    title: string,
    hasMarginLeft = false
  ) => {
    return (
      <TouchableOpacity onPress={onPress}>
        <WebHoverBox
          ml={hasMarginLeft ? 'l' : 'none'}
          mb='m'
          borderRadius='xs'
          width={(itemWidth - theme.spacing.l) / 2}
          flexDirection='row'
          height={52}
          backgroundColor='grey01'
          hoverBackgroundColor='grey02'
          alignItems='center'>
          <Box width={20} height={20}></Box>
          <Icon color='greenSecondary' variant='s' name='Plus'></Icon>
          <Text ml='s' mr='m' variant='labelSmall' color='greenSecondary'>
            {title}
          </Text>
        </WebHoverBox>
      </TouchableOpacity>
    );
  };

  return (
    <>
      {confirmModalVisible && (
        <ProjectChecklistCenterModal
          contentNode={
            <Box m='l'>
              <Box flexDirection='row' marginBottom='l' alignItems='center'>
                <Text
                  flex={1}
                  style={{ alignSelf: 'flex-start' }}
                  variant='labelLarge'
                  color='textPrimary'>
                  {t('models:projects.checklist.readyToConvert')}
                </Text>
                <Icon
                  name='X'
                  variant='m'
                  onPress={() => {
                    convertFromTooltipRef.current = false;
                    setConfirmModalVisible(false);
                  }}
                />
              </Box>

              <Text
                marginBottom='l'
                variant='webBodySecondary'
                color='textPrimary'
                style={{ alignSelf: 'flex-start' }}>
                {t(
                  'models:projects.checklist.theseItemsWereSavedInYourChecklist'
                )}
              </Text>

              <Box style={{ alignSelf: 'flex-end' }} width={125}>
                <Button
                  height={48}
                  fullWidth={true}
                  variant='primary'
                  onPress={() => {
                    setConfirmModalVisible(false);
                    doConvertToTasks(
                      checklistItems
                        .filter((item) => item.checked)
                        .map((item1) => item1.id)
                    );
                  }}
                  accessibilityLabel={t('models:projects.checklist.convert')}>
                  <Text variant='labelSmall' color='white'>
                    {t('models:projects.checklist.convert')}
                  </Text>
                </Button>
              </Box>
            </Box>
          }
          onClose={() => {
            convertFromTooltipRef.current = false;
            setConfirmModalVisible(false);
          }}
          visible={confirmModalVisible}
        />
      )}
      {selectChecklistModalVisible && (
        <ProjectChecklistCenterModal
          padding='none'
          width={504}
          onClose={() => {
            convertFromTooltipRef.current = false;
            setSelectChecklistModalVisible(false);
          }}
          visible={selectChecklistModalVisible}
          contentNode={
            <Box py='l' backgroundColor='white' maxHeight={420}>
              <Box
                flexDirection='row'
                marginBottom='m'
                alignItems='center'
                px='l'>
                <Text
                  flex={1}
                  style={{ alignSelf: 'flex-start' }}
                  variant='labelLarge'
                  color='textPrimary'>
                  {t('models:projects.checklist.selectChecklist')}
                </Text>
                <Icon
                  name='X'
                  variant='m'
                  onPress={() => {
                    convertFromTooltipRef.current = false;
                    setSelectChecklistModalVisible(false);
                  }}
                />
              </Box>

              {checklistItems.length > 0 && (
                <SelectChecklistRow
                  checked={allChecklistItemsChecked}
                  indeterminate={someChecklistItemsChecked}
                  onPress={toggleAllItemsChecked}>
                  <Text ml='xs' variant='bodySecondaryBold' color='textPrimary'>
                    {t('shared:selectAll')}
                  </Text>
                </SelectChecklistRow>
              )}

              <FlashList
                showsVerticalScrollIndicator={false}
                showsHorizontalScrollIndicator={false}
                ListFooterComponent={
                  <SelectChecklistEmptyFooter
                    isEmpty={checklistItems.length === 0}
                  />
                }
                data={checklistItems}
                estimatedItemSize={52}
                renderItem={({ item }) => (
                  <SelectChecklistRow
                    key={item.id}
                    checked={item.checked}
                    onPress={() => toggleItemChecked(item.id)}>
                    <Text
                      ml='xs'
                      variant='bodySecondary'
                      color='textPrimary'
                      numberOfLines={2}
                      ellipsizeMode='tail'
                      textAlign='left'
                      style={{ lineHeight: 16, maxWidth: 428 }}>
                      {item.name}
                    </Text>
                  </SelectChecklistRow>
                )}
              />
              <Box mt='m' mr='l' style={{ alignSelf: 'flex-end' }} width={107}>
                <Button
                  disabled={disabledSelectChecklistConfirmButton}
                  height={48}
                  fullWidth={true}
                  variant='primary'
                  onPress={() => {
                    if (checklistItems.length > 0) {
                      setSelectChecklistModalVisible(false);
                      setConfirmModalVisible(true);
                    }
                  }}
                  accessibilityLabel={t('shared:confirm')}>
                  <Text
                    variant='labelSmall'
                    color={
                      disabledSelectChecklistConfirmButton ? 'grey04' : 'white'
                    }>
                    {t('shared:confirm')}
                  </Text>
                </Button>
              </Box>
            </Box>
          }></ProjectChecklistCenterModal>
      )}
      {modalVisible && (
        <ProjectChecklistCenterModal
          contentNode={
            <Box p='m'>
              <Box flexDirection='row' marginBottom='m' alignItems='center'>
                <Text
                  flex={1}
                  style={{ alignSelf: 'flex-start' }}
                  variant='heading2'
                  color='textPrimary'>
                  {errorTitle}
                </Text>
                <TouchableOpacity
                  style={{ outline: 'none' }}
                  onPress={() => {
                    setModalVisible(false);
                  }}>
                  <Icon name='X' variant='m' />
                </TouchableOpacity>
              </Box>

              <Text
                marginBottom='m'
                variant='bodySecondary'
                color='textSecondary'
                style={{ alignSelf: 'flex-start' }}>
                {errorMsg}
              </Text>

              <TouchableOpacity
                style={{ outline: 'none' }}
                onPress={() => {
                  setErrorMsg('');
                  setModalVisible(false);
                }}>
                <Text
                  style={{ alignSelf: 'flex-end' }}
                  variant='buttonLabel'
                  color='greenSecondary'>
                  {t('models:projects.checklist.gotIt')}
                </Text>
              </TouchableOpacity>
            </Box>
          }
          onClose={() => {
            setModalVisible(false);
          }}
          visible={modalVisible}
        />
      )}

      <Box flex={1}>
        {/* ── Top action bar ── */}
        <Box
          accessibilityLabel='Project Checklist Bar'
          mt='xs'
          mb='xs'
          flexDirection='row'
          justifyContent='space-between'>
          <Box flexDirection='row' ml='m'>
            {buttonDisableMode
              ? disableButton(getScaledSize(174), false)
              : titleBarButton(
                  t('projects.checklist.saveAsTemplate'),
                  'Save',
                  () => {
                    if (shouldShowPaywallForProject(project)) {
                      setPaywallTeam(project?.team ?? undefined);
                      setPaywallModalOpen(true);
                      return;
                    }
                    setIdProjectSaveChecklistAsTemplateOpen(projectUUId);
                  }
                )}

            {!disableMode && (
              <TooltipOnboardingWeb
                variant='bottom-left'
                tooltipStyle='success'
                title={t('projects.checklist.convertToTasksTooltip.title')}
                description={t(
                  'projects.checklist.convertToTasksTooltip.description'
                )}
                step={t('projects.checklist.convertToTasksTooltip.step')}
                open={showConvertToTasksTooltip}
                forceShow={showConvertToTasksTooltip}
                arrowAtTriggerCenter>
                {titleBarButton(
                  t('projects.checklist.convertToTask'),
                  'ListChecks',
                  () => {
                    if (showConvertToTasksTooltip) {
                      setShowConvertToTasksTooltip(false);
                      convertFromTooltipRef.current = true;
                    }
                    doRestChecklistItems();
                    setSelectChecklistModalVisible(true);
                  }
                )}
              </TooltipOnboardingWeb>
            )}
          </Box>

          {hasCompletedItems && (
            <Box flexDirection='row' alignItems='center' mx='m'>
              <Text
                flex={1}
                textAlign='right'
                mr='xs'
                variant='webSmall'
                color='textSecondary'>
                Show completed items
              </Text>
              <ToggleSwitch
                boxRadius={10}
                switchWidth={35}
                circleWidth={14}
                switchHeight={20}
                onValueChange={async (isEnabled) => {
                  setShowChecklistCompletedItems(isEnabled);
                }}
                value={showChecklistCompletedItems}
              />
            </Box>
          )}
        </Box>

        {/* ── Checklist body ── */}
        <Box
          onLayout={(e) => {
            setItemWidth(e.nativeEvent.layout.width);
          }}
          flexShrink={1}
          flexGrow={0}
          flexBasis='auto'
          maxHeight='100%'
          mx='m'
          mt='s'>
          <ScrollView
            ref={scrollViewRef}
            style={{ flexGrow: 0 }}
            contentContainerStyle={{ flexGrow: 1 }}
            showsHorizontalScrollIndicator={false}
            showsVerticalScrollIndicator={false}>
            {/* DnD sortable list — active + (optionally) completed items */}

            <DndKitSortableProjectChecklistListWeb
              // itemMarginBottom='none'
              width={itemWidth}
              canEdit={canEdit}
              listProjectChecklist={[
                ...checklist,
                ...(showChecklistCompletedItems ? completedChecklist : []),
              ]}
              showChecklistCompletedItems={showChecklistCompletedItems}
              onCompletedProjectChecklistItem={handleCompletedChecklistItem}
              onDeleteProjectChecklistItem={handleDeleteChecklistItem}
              onSaveProjectChecklistItem={handleSaveChecklistItem}
              onReorder={onDragEnd}
            />

            {!disableMode && showAddCheckListItem && (
              <Box mt='m'>
                <ProjectAddChecklistItem
                  setShowInput={setShowAddCheckListItem}
                  width={itemWidth}
                  onSaveProjectChecklistItem={handleSaveChecklistItem}
                />
              </Box>
            )}
            {/* Saving / reorder spinner */}
            {processing && (
              <Box
                flexDirection='row'
                alignItems='center'
                justifyContent='center'
                mt='m'>
                <ActivityIndicator color={theme.colors.textPrimary} size={16} />
              </Box>
            )}
          </ScrollView>
        </Box>
        {/* Add item row — hidden in read-only mode */}
        {!disableMode && !showAddCheckListItem && (
          <Box mx='m' pt='m' flexDirection='row'>
            {addButton(() => {
              setShowAddCheckListItem(true);
              setTimeout(() => {
                scrollViewRef.current?.scrollToEnd({ animated: true });
              }, 100);
            }, t('tasks.buttons.addItem'))}
            {addButton(
              () => {
                if (shouldShowPaywallForProject(project)) {
                  setPaywallTeam(project?.team ?? undefined);
                  setPaywallModalOpen(true);
                  return;
                }
                setIdProjectChecklistTemplateOpen(projectUUId);
              },
              t('projects.checklist.addItemFromTemplate'),
              true
            )}
          </Box>
        )}
      </Box>
    </>
  );
};
